import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import FormInput from 'shared/components/forms/FormInput';
import FormPhoneInput from 'shared/components/forms/FormPhoneInput';
import FormTextarea from 'shared/components/forms/FormTextarea';
import OwnerContactToggle from 'features/onboarding/components/OwnerContactToggle';
import { representativeSchema, type RepresentativeInput } from 'features/onboarding/schemas';
import { useStepForm } from 'features/onboarding/hooks/useStepForm';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useRegisterStepNav } from 'features/onboarding/context/StepNavContext';

const Step6Representative: React.FC = () => {
  const { t } = useTranslation();
  const { data, updateSection } = useOnboarding();
  const { businessInfo, representativeInfo } = data;

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    submit,
    goBack,
  } = useStepForm<RepresentativeInput>({
    section: 'representativeInfo',
    schema: representativeSchema,
  });

  useRegisterStepNav({
    onBack: goBack,
    onNext: submit,
    nextLabel: t('nav.representative') as string,
  });

  const matchesOwner =
    !!representativeInfo.representativeName &&
    representativeInfo.representativeName === businessInfo.ownerName &&
    representativeInfo.representativePhone === businessInfo.phoneNumber;

  const [isOwner, setIsOwner] = useState<boolean>(matchesOwner);

  useEffect(() => {
    if (matchesOwner) setIsOwner(true);
  }, [matchesOwner]);

  const handleToggle = (next: boolean) => {
    setIsOwner(next);
    const opts = { shouldValidate: true, shouldDirty: true } as const;
    if (next) {
      setValue('representativeName', businessInfo.ownerName ?? '', opts);
      setValue('representativePhone', businessInfo.phoneNumber ?? '', opts);
    } else {
      setValue('representativeName', '', opts);
      setValue('representativePhone', '', opts);
    }
  };

  return (
    <StepCard title={t('representative.title')} subtitle={t('representative.subtitle') as string}>
      <div className="mb-4">
        <OwnerContactToggle
          checked={isOwner}
          onChange={handleToggle}
          label={t('representative.isOwnerToggleLabel') as string}
          hint={t('representative.isOwnerToggleHint') as string}
        />
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <FormInput
            label={t('representative.representativePosition')}
            placeholder={t('representative.representativePositionPlaceholder') as string}
            required
            error={errors.representativePosition?.message}
            {...register('representativePosition')}
          />
        </div>
        <FormInput
          label={t('representative.representativeName')}
          placeholder={t('representative.representativeNamePlaceholder') as string}
          required
          error={errors.representativeName?.message}
          {...register('representativeName')}
        />
        <FormPhoneInput
          label={t('representative.representativePhone')}
          required
          error={errors.representativePhone?.message}
          value={watch('representativePhone') ?? ''}
          onChange={(value) =>
            setValue('representativePhone', value, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        />

        <div className="sm:col-span-2">
          <FormTextarea
            label={t('finalDetails.notes')}
            placeholder={t('finalDetails.notesPlaceholder') as string}
            rows={4}
            value={data.finalDetails.notes}
            onChange={(e) => updateSection('finalDetails', { notes: e.target.value })}
          />
        </div>
      </form>
    </StepCard>
  );
};

export default Step6Representative;
